import React, {createContext, ReactNode, useContext, useReducer, useState} from "react";
import {OverlayType} from "../types/enums";
import {CustomOverlayData, OverlayResults} from "../types/overlay";
import {Region} from "../types/Types";

interface OverlayContextType {
  modalDisplay: boolean;
  showModal: () => void,
  hideModal: () => void,
  overlayType: OverlayType,
  setOverlayType: (overlayType: OverlayType) => void,
  overlayScreen: string | null,
  setOverlayScreen: (overlayScreen: string | null) => void,
  customOverlayLists: Array<CustomOverlayData>,
  setCustomOverlayLists: (customOverlayLists: Array<CustomOverlayData>) => void,
  processOverlay: number,
  triggerProcessOverlay: () => void,
  overlayResults: OverlayResults | undefined,
  setOverlayResults: (overlayResults: OverlayResults | undefined) => void,
  hoveredRegions: Region[],
  setHoveredRegions: (regions: Region[]) => void,
}

const OverlayContext = createContext<OverlayContextType | undefined>(undefined);

interface OverlayProviderProps {
  children: ReactNode;
}

export const OverlayProvider: React.FC<OverlayProviderProps> = ({children}) => {
  const [modalDisplay, setModalDisplay] = useState(false);
  const [overlayType, setOverlayType] = useState(OverlayType.SAFE);
  const [processOverlay, triggerProcessOverlay] = useReducer(x => x + 1, 0);

  const [overlayScreen, setOverlayScreen] = useState<string | null>(null);
  const [customOverlayLists, setCustomOverlayLists] = useState<Array<CustomOverlayData>>([]);
  const [overlayResults, setOverlayResults] = useState<OverlayResults | undefined>(undefined);
  const [hoveredRegions, setHoveredRegions] = useState<Region[]>([]);

  const showModal = () => setModalDisplay(true);
  const hideModal = () => setModalDisplay(false);

  return (
    <OverlayContext.Provider
      value={{
        modalDisplay,
        showModal,
        hideModal,
        overlayType,
        setOverlayType,
        overlayScreen,
        setOverlayScreen,
        customOverlayLists,
        setCustomOverlayLists,
        processOverlay,
        triggerProcessOverlay,
        overlayResults,
        setOverlayResults,
        hoveredRegions,
        setHoveredRegions,
      }}
    >
      {children}
    </OverlayContext.Provider>
  );
};

export const useOverlayContext = () => {
  const context = useContext(OverlayContext);
  if (!context) {
    throw new Error('useOverlayContext must be used within an OverlayProvider');
  }
  return context;
};
